import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Divider,
  Chip,
  TextField,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  ShoppingCart as CartIcon,
  ArrowForward as ArrowForwardIcon,
  LocalOffer as CouponIcon,
  LocalShipping as ShippingIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { cartService, shippingService } from '../services/api';
import { toast } from 'react-toastify';

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [total, setTotal] = useState(0);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [shippingCharges, setShippingCharges] = useState(0);
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();
  const { loadCart } = useCart();

  useEffect(() => {
    if (authLoading) return; // Wait for auth to complete
    
    if (!currentUser) {
      navigate('/login');
      return;
    }
    fetchCart();
  }, [currentUser, authLoading, navigate]);

  const fetchCart = async () => {
    try {
      setLoading(true);
      const response = await cartService.getCart();
      
      const cart = response.data.cart || { items: [], total: 0 };
      console.log('Cart.js: Extracted cart object:', cart);
      console.log('Cart.js: Cart items:', cart.items);
      
      // Debug each cart item
      cart.items.forEach((item, index) => {
        console.log(`Cart.js: Item ${index}:`, item);
        console.log(`Cart.js: Item ${index} image:`, item.image);
        console.log(`Cart.js: Item ${index} name:`, item.name);
        console.log(`Cart.js: Item ${index} productName:`, item.productName);
        console.log(`Cart.js: Item ${index} all keys:`, Object.keys(item));
      });
      
      setCartItems(cart.items || []);
      setTotal(cart.total || 0);
    } catch (error) {
      console.error('Error fetching cart:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Failed to load cart');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, newQuantity, customizations = {}) => {
    if (newQuantity < 1) return;
    const key = productId + '__' + JSON.stringify(customizations||{});
    try {
      setUpdating(prev => ({ ...prev, [key]: true }));
      // Optimistic local update
      setCartItems(prev => prev.map(item => (
        item.productId === productId && JSON.stringify(item.customizations||{}) === JSON.stringify(customizations||{})
          ? { ...item, quantity: newQuantity }
          : item
      )));
      // Recompute total locally
      setTotal(prev => {
        const subtotal = cartItems.reduce((sum, item) => {
          if (item.productId === productId && JSON.stringify(item.customizations||{}) === JSON.stringify(customizations||{})) {
            return sum + (item.price * newQuantity);
          }
            return sum + (item.price * item.quantity);
        }, 0);
        return subtotal;
      });
      await cartService.updateCart({ productId, quantity: newQuantity, customizations: customizations || {} });
      // Refresh shared cart count silently
      loadCart();
    } catch (error) {
      console.error('Error updating quantity:', error);
      toast.error('Failed to update cart, reverting');
      // Fallback: full refetch to restore consistency
      fetchCart();
    } finally {
      setUpdating(prev => ({ ...prev, [key]: false }));
    }
  };

  const removeItem = async (productId, customizations = {}) => {
    if (!window.confirm('Remove this item from cart?')) return;
    const key = productId + '__' + JSON.stringify(customizations||{});
    // Snapshot previous state for rollback
    const prevItems = cartItems;
    try {
      setUpdating(prev => ({ ...prev, [key]: true }));
      // Optimistic removal
      const filtered = cartItems.filter(item => !(
        item.productId === productId && JSON.stringify(item.customizations||{}) === JSON.stringify(customizations||{})
      ));
      setCartItems(filtered);
      setTotal(filtered.reduce((sum,i)=> sum + i.price * i.quantity, 0));
      await cartService.removeFromCart(productId, customizations);
      loadCart();
      toast.success('Item removed from cart');
    } catch (error) {
      console.error('Error removing item:', error);
      toast.error('Failed to remove item, reverting');
      // Rollback
      setCartItems(prevItems);
      setTotal(prevItems.reduce((sum,i)=> sum + i.price * i.quantity, 0));
    } finally {
      setUpdating(prev => ({ ...prev, [key]: false }));
    }
  };

  const clearCart = async () => {
    if (!window.confirm('Clear entire cart?')) return;
    
    try {
      setLoading(true);
      await cartService.clearCart();
      setCartItems([]);
      setTotal(0);
      toast.success('Cart cleared');
    } catch (error) {
      console.error('Error clearing cart:', error);
      toast.error('Failed to clear cart');
    } finally {
      setLoading(false);
    }
  };

  // Fetch shipping charges from admin settings
  const fetchShippingCharges = async () => {
    try {
      const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      const res = await shippingService.getQuote({ subtotal });
      // Use first option as default
      const first = res.data?.options?.[0];
      setShippingCharges(first ? first.price : 0);
    } catch (error) {
      console.error('Error fetching shipping charges:', error);
      setShippingCharges(0);
    }
  };

  // Apply coupon code
  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    setCouponLoading(true);
    try {
      // Prepare cart data for coupon validation
      const cartData = {
        items: cartItems.map(item => ({
          productId: item.productId,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          category: item.category,
          customizations: item.customizations
        })),
        subtotal: total,
        shippingCharges: shippingCharges
      };

      // Call API to validate and apply coupon
      const response = await cartService.validateCoupon(couponCode.trim(), cartData);
      
      if (response.data.valid) {
        setAppliedCoupon({
          code: response.data.coupon.code,
          type: response.data.coupon.type,
          value: response.data.coupon.value,
          description: response.data.coupon.description,
          discount: response.data.discount
        });
        toast.success(`Coupon applied: ${response.data.coupon.description}`);
      } else {
        toast.error('Invalid coupon code');
      }
    } catch (error) {
      console.error('Error applying coupon:', error);
      const errorMessage = error.response?.data?.error || 'Failed to apply coupon';
      toast.error(errorMessage);
    } finally {
      setCouponLoading(false);
    }
  };

  // Remove applied coupon
  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast.success('Coupon removed');
  };

  // Calculate pricing breakdown
  const calculatePricing = () => {
    const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    // Calculate tax (18% is included in product price, so we need to extract it)
    const taxIncludedAmount = subtotal;
    const taxRate = 0.18;
    const baseAmount = taxIncludedAmount / (1 + taxRate);
    const taxAmount = taxIncludedAmount - baseAmount;
    
    // Apply coupon discount (use the discount value from API validation)
    let discount = 0;
    let finalShipping = shippingCharges;
    
    if (appliedCoupon) {
      if (appliedCoupon.type === 'shipping') {
        finalShipping = 0;
        discount = shippingCharges; // Show shipping amount as discount
      } else {
        // Use the discount value calculated by the backend
        discount = appliedCoupon.discount || 0;
      }
    }
    
    const finalTotal = subtotal - discount + finalShipping;
    
    return {
      subtotal,
      baseAmount,
      taxAmount,
      discount,
      shipping: finalShipping,
      total: finalTotal
    };
  };

  // Fetch shipping charges when component mounts
  useEffect(() => {
    if (cartItems.length > 0) {
      fetchShippingCharges();
    }
  }, [cartItems]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (cartItems.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Paper elevation={2} sx={{ p: 6, textAlign: 'center' }}>
          <CartIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Your Cart is Empty
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Looks like you haven't added any items to your cart yet.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/products')}
            sx={{ mt: 2 }}
          >
            Continue Shopping
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" fontWeight={600}>
          Shopping Cart
        </Typography>
        <Button
          variant="outlined"
          color="error"
          onClick={clearCart}
          disabled={loading}
        >
          Clear Cart
        </Button>
      </Box>

      <Grid container spacing={4}>
        {/* Cart Items */}
        <Grid item xs={12} md={8}>
          {cartItems.map((item) => (
            <Card key={item.productId} sx={{ mb: 2 }}>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  {/* Product Image */}
                  <Grid item xs={12} sm={3}>
                    {(() => {
                      console.log(`Rendering image for item:`, item);
                      console.log(`Image URL: ${item.image || '/placeholder-image.jpg'}`);
                      return null;
                    })()}
                    <CardMedia
                      component="img"
                      height="120"
                      image={item.image || '/placeholder-image.jpg'}
                      alt={item.productName || item.name}
                      sx={{ borderRadius: 1, objectFit: 'cover' }}
                    />
                  </Grid>

                  {/* Product Details */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="h6" gutterBottom>
                      {item.name}
                    </Typography>
                    <Box mb={1}>
                      {item.salePrice && item.salePrice < item.originalPrice ? (
                        <Typography variant="body2" color="text.secondary">
                          <strong>₹{item.price}</strong>{' '}
                          <span style={{ textDecoration: 'line-through', color: '#999', marginLeft: 4 }}>₹{item.originalPrice}</span>{' '}
                          <Chip size="small" color="error" label={`${Math.round(((item.originalPrice - item.price)/item.originalPrice)*100)}% OFF`} sx={{ ml: 1 }} />
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Price: ₹{item.price}
                        </Typography>
                      )}
                    </Box>

                    {/* Customization Details */}
                    {(item.customizations && (item.customizations.text || (item.customizations.images && item.customizations.images.length))) && (
                      <Box mt={1}>
                        {item.customizations.text && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Custom Text:</strong> {item.customizations.text}
                          </Typography>
                        )}
                        {Array.isArray(item.customizations.images) && item.customizations.images.length > 0 && (
                          <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
                            {item.customizations.images.map((imgUrl, idx) => (
                              <img
                                key={idx}
                                src={imgUrl}
                                alt={`Custom ${idx+1}`}
                                style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd' }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Grid>

                  {/* Quantity Controls */}
                  <Grid item xs={12} sm={3}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box display="flex" alignItems="center">
                        <IconButton
                          onClick={() => updateQuantity(item.productId, item.quantity - 1, item.customizations)}
                          disabled={updating[item.productId] || item.quantity <= 1}
                          size="small"
                        >
                          <RemoveIcon />
                        </IconButton>
                        <TextField
                          value={item.quantity}
                          size="small"
                          sx={{ width: 60, mx: 1 }}
                          inputProps={{ 
                            min: 1, 
                            style: { textAlign: 'center' },
                            readOnly: true
                          }}
                        />
                        <IconButton
                          onClick={() => updateQuantity(item.productId, item.quantity + 1, item.customizations)}
                          disabled={updating[item.productId]}
                          size="small"
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>
                      <IconButton
                        onClick={() => removeItem(item.productId, item.customizations)}
                        disabled={updating[item.productId]}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <Typography variant="h6" textAlign="center" mt={1}>
                      ₹{(item.price * item.quantity).toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Grid>

        {/* Order Summary */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, position: 'sticky', top: 20 }}>
            <Typography variant="h6" gutterBottom>
              Order Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {cartItems.map((item) => (
              <Box key={item.productId} display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">
                  {item.name} × {item.quantity}
                </Typography>
                <Typography variant="body2">
                  ₹{(item.price * item.quantity).toFixed(2)}
                </Typography>
              </Box>
            ))}
            
            <Divider sx={{ my: 2 }} />
            
            {/* Coupon Section */}
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>
                <CouponIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Apply Coupon
              </Typography>
              
              {!appliedCoupon ? (
                <Box display="flex" gap={1}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    onKeyPress={(e) => e.key === 'Enter' && applyCoupon()}
                  />
                  <Button
                    variant="contained"
                    onClick={applyCoupon}
                    disabled={couponLoading || !couponCode.trim()}
                    sx={{ minWidth: '100px' }}
                  >
                    {couponLoading ? 'Applying...' : 'Apply'}
                  </Button>
                </Box>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="space-between" 
                     sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                  <Box display="flex" alignItems="center">
                    <CouponIcon color="success" sx={{ mr: 1 }} />
                    <Typography variant="body2" color="success.dark">
                      <strong>{appliedCoupon.code}</strong> - {appliedCoupon.description}
                    </Typography>
                  </Box>
                  <Button size="small" onClick={removeCoupon} color="error">
                    Remove
                  </Button>
                </Box>
              )}
              
            </Box>

            {/* Price Breakdown */}
            {(() => {
              const pricing = calculatePricing();
              return (
                <Box>
                  <Typography variant="h6" gutterBottom>Order Summary</Typography>
                  
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>Subtotal ({cartItems.length} items)</Typography>
                    <Typography>₹{pricing.subtotal.toFixed(2)}</Typography>
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between" mb={1} sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                    <Typography variant="body2">Base Amount (excl. tax)</Typography>
                    <Typography variant="body2">₹{pricing.baseAmount.toFixed(2)}</Typography>
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between" mb={1} sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                    <Typography variant="body2">Tax (18% included)</Typography>
                    <Typography variant="body2">₹{pricing.taxAmount.toFixed(2)}</Typography>
                  </Box>
                  
                  {pricing.discount > 0 && (
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="success.main">Coupon Discount</Typography>
                      <Typography color="success.main">-₹{pricing.discount.toFixed(2)}</Typography>
                    </Box>
                  )}
                  
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Box display="flex" alignItems="center">
                      <ShippingIcon sx={{ mr: 1, fontSize: '1rem' }} />
                      <Typography>Shipping Charges</Typography>
                    </Box>
                    <Typography color={pricing.shipping === 0 ? 'success.main' : 'inherit'}>
                      {pricing.shipping === 0 ? 'Free' : `₹${pricing.shipping.toFixed(2)}`}
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography variant="h6">Total Amount</Typography>
                    <Typography variant="h6" color="primary">
                      ₹{pricing.total.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              );
            })()}
            
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => navigate('/checkout')}
              sx={{ mt: 2 }}
            >
              Proceed to Checkout
            </Button>
            
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate('/products')}
              sx={{ mt: 1 }}
            >
              Continue Shopping
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Cart;
